import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Plus, Trash2 } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  Toggle,
  FormActions,
  CollapsibleSection,
  ImageUpload,
  ImageUploadWithAlt,
  CTAGroup,
  BackgroundControls,
  ReorderButtons,
  autoSlug,
} from "../../components/FormField";
import { initialPages } from "../../lib/mockData";
import { showSuccess, showError } from "../../lib/toast";

const moveItem = (arr, from, to) => {
  const a = [...arr];
  const [item] = a.splice(from, 1);
  a.splice(to, 0, item);
  return a;
};

const emptyCTA = () => ({ text: "", url: "", newTab: false });
const emptyBG = () => ({ imageAlt: "", videoUrl: "", overlayOpacity: 0 });

export function HomePageForm() {
  const navigate = useNavigate();
  const existing = initialPages.find((p) => p.template === "Homepage");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Basic
  const [title, setTitle] = useState(existing?.title ?? "Home");
  const [slug, setSlug] = useState(existing?.slug ?? "/");
  const [seoTitle, setSeoTitle] = useState(existing?.seoTitle ?? "");
  const [seoDesc, setSeoDesc] = useState(existing?.seoDescription ?? "");
  const [seoCanonical, setSeoCanonical] = useState("");
  const [seoIndex, setSeoIndex] = useState(true);

  // Section toggles
  const [sections, setSections] = useState({
    hero: true,
    solutions: true,
    industries: true,
    services: true,
    products: true,
    whyChoose: true,
    insights: true,
    clientSuccess: true,
    finalCta: true,
    seo: true,
  });
  const toggle = (k) => setSections((s) => ({ ...s, [k]: !s[k] }));

  // ── HERO ──
  const [heroEyebrow, setHeroEyebrow] = useState("");
  const [heroHeading, setHeroHeading] = useState(existing?.heroHeading ?? "");
  const [heroHighlight, setHeroHighlight] = useState("");
  const [heroSubheading, setHeroSubheading] = useState(
    existing?.heroSubheading ?? "",
  );
  const [heroPrimaryCta, setHeroPrimaryCta] = useState({
    text: existing?.heroCTA ?? "",
    url: "",
    newTab: false,
  });
  const [heroSecondaryCta, setHeroSecondaryCta] = useState(emptyCTA());
  const [heroBg, setHeroBg] = useState(emptyBG());
  const [heroStats, setHeroStats] = useState([
    { number: "", label: "", icon: "" },
  ]);

  // ── SOLUTIONS ──
  const [solLabel, setSolLabel] = useState("");
  const [solHeading, setSolHeading] = useState("");
  const [solDescription, setSolDescription] = useState("");
  const [solCards, setSolCards] = useState([
    { title: "", description: "", imageAlt: "", link: "", category: "" },
  ]);
  const [solBg, setSolBg] = useState(emptyBG());

  // ── INDUSTRIES ──
  const [indHeading, setIndHeading] = useState("");
  const [indItems, setIndItems] = useState([
    { iconAlt: "", title: "", description: "", link: "" },
  ]);
  const [indBg, setIndBg] = useState(emptyBG());

  // ── SERVICES ──
  const [svcHeading, setSvcHeading] = useState("");
  const [svcCards, setSvcCards] = useState([
    { title: "", description: "", imageAlt: "", link: "" },
  ]);
  const [svcBg, setSvcBg] = useState(emptyBG());

  // ── PRODUCTS ──
  const [prodHeading, setProdHeading] = useState("");
  const [prodSelected, setProdSelected] = useState("");
  const [prodBg, setProdBg] = useState(emptyBG());

  // ── WHY CHOOSE US ──
  const [whyHeading, setWhyHeading] = useState("");
  const [whyCards, setWhyCards] = useState([
    { iconAlt: "", title: "", description: "" },
  ]);
  const [whyBg, setWhyBg] = useState(emptyBG());

  // ── INSIGHTS ──
  const [insHeading, setInsHeading] = useState("");
  const [insMode, setInsMode] = useState("auto");
  const [insSelectedPosts, setInsSelectedPosts] = useState("");
  const [insBg, setInsBg] = useState(emptyBG());

  // ── CLIENT SUCCESS ──
  const [csTestimonials, setCsTestimonials] = useState([
    { quote: "", author: "", designation: "", company: "", imageAlt: "" },
  ]);
  const [csBg, setCsBg] = useState(emptyBG());

  // ── FINAL CTA ──
  const [fctaHeading, setFctaHeading] = useState("");
  const [fctaSubtext, setFctaSubtext] = useState("");
  const [fctaPrimary, setFctaPrimary] = useState(emptyCTA());
  const [fctaSecondary, setFctaSecondary] = useState(emptyCTA());
  const [fctaBg, setFctaBg] = useState(emptyBG());

  const handleTitleChange = (v) => {
    setTitle(v);
    setSlug(autoSlug(v));
  };

  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    return e;
  };

  const handleSave = (status) => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      showError("Please fix the errors");
      return;
    }
    setErrors({});
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showSuccess("Home page updated!", `Saved as ${status}.`);
      navigate("/pages");
    }, 800);
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/pages")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">Edit Home Page</h1>
            <p className="page-subtitle">Manage all sections of the homepage</p>
          </div>
        </div>
        <button className="flex items-center gap-2 text-sm font-medium text-muted-foreground border border-border rounded-lg px-3 py-2 hover:bg-muted transition-colors">
          <Eye className="w-4 h-4" /> Preview
        </button>
      </div>

      {/* Basic Settings */}
      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Basic Settings</h2>
        </div>
        <div className="section-panel-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Page Title" required error={errors.title}>
              <TextInput
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Home"
                error={!!errors.title}
              />
            </Field>
            <Field label="Slug" hint="Auto-generated from title, editable">
              <TextInput
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="/"
              />
            </Field>
          </div>
        </div>
      </div>

      {/* ── HERO ── */}
      <CollapsibleSection
        title="Hero Section"
        active={sections.hero}
        onToggleActive={() => toggle("hero")}
      >
        <Field label="Eyebrow Label">
          <TextInput
            value={heroEyebrow}
            onChange={(e) => setHeroEyebrow(e.target.value)}
            placeholder="e.g. Enterprise ERP Solutions"
          />
        </Field>
        <Field label="Main Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
            placeholder="Transform Your Business"
          />
        </Field>
        <Field label="Highlight Word" hint="Word in heading to accent">
          <TextInput
            value={heroHighlight}
            onChange={(e) => setHeroHighlight(e.target.value)}
            placeholder="Transform"
          />
        </Field>
        <Field label="Subheading">
          <TextArea
            value={heroSubheading}
            onChange={(e) => setHeroSubheading(e.target.value)}
            placeholder="Supporting text"
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimaryCta.text}
          url={heroPrimaryCta.url}
          newTab={heroPrimaryCta.newTab}
          onTextChange={(v) => setHeroPrimaryCta((c) => ({ ...c, text: v }))}
          onUrlChange={(v) => setHeroPrimaryCta((c) => ({ ...c, url: v }))}
          onNewTabChange={(v) =>
            setHeroPrimaryCta((c) => ({ ...c, newTab: v }))
          }
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondaryCta.text}
          url={heroSecondaryCta.url}
          newTab={heroSecondaryCta.newTab}
          onTextChange={(v) => setHeroSecondaryCta((c) => ({ ...c, text: v }))}
          onUrlChange={(v) => setHeroSecondaryCta((c) => ({ ...c, url: v }))}
          onNewTabChange={(v) =>
            setHeroSecondaryCta((c) => ({ ...c, newTab: v }))
          }
        />

        {/* Stats Repeater */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Stats
            </p>
            <button
              type="button"
              onClick={() =>
                setHeroStats((s) => [...s, { number: "", label: "", icon: "" }])
              }
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add Stat
            </button>
          </div>
          {heroStats.map((st, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Stat {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={heroStats.length}
                    onMove={(f, t) => setHeroStats(moveItem(heroStats, f, t))}
                  />
                  {heroStats.length > 1 && (
                    <button
                      onClick={() =>
                        setHeroStats((s) => s.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Number">
                  <TextInput
                    value={st.number}
                    onChange={(e) =>
                      setHeroStats((s) =>
                        s.map((x, idx) =>
                          idx === i ? { ...x, number: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="500+"
                  />
                </Field>
                <Field label="Label">
                  <TextInput
                    value={st.label}
                    onChange={(e) =>
                      setHeroStats((s) =>
                        s.map((x, idx) =>
                          idx === i ? { ...x, label: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="Clients"
                  />
                </Field>
                <Field label="Icon">
                  <ImageUpload label="Upload icon" hint="SVG or PNG" />
                </Field>
              </div>
            </div>
          ))}
        </div>

        <BackgroundControls
          bgImageAlt={heroBg.imageAlt}
          onBgImageAltChange={(v) => setHeroBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={heroBg.videoUrl}
          onBgVideoUrlChange={(v) => setHeroBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={heroBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setHeroBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── SOLUTIONS ── */}
      <CollapsibleSection
        title="Solutions For Every Business Challenge"
        active={sections.solutions}
        onToggleActive={() => toggle("solutions")}
      >
        <Field label="Section Label">
          <TextInput
            value={solLabel}
            onChange={(e) => setSolLabel(e.target.value)}
            placeholder="Our Solutions"
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={solHeading}
            onChange={(e) => setSolHeading(e.target.value)}
            placeholder="Solutions for Every Challenge"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={solDescription}
            onChange={(e) => setSolDescription(e.target.value)}
            placeholder="Brief intro"
          />
        </Field>
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Solution Cards
            </p>
            <button
              type="button"
              onClick={() =>
                setSolCards((c) => [
                  ...c,
                  {
                    title: "",
                    description: "",
                    imageAlt: "",
                    link: "",
                    category: "",
                  },
                ])
              }
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add Card
            </button>
          </div>
          {solCards.map((c, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Card {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={solCards.length}
                    onMove={(f, t) => setSolCards(moveItem(solCards, f, t))}
                  />
                  {solCards.length > 1 && (
                    <button
                      onClick={() =>
                        setSolCards((s) => s.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Title">
                <TextInput
                  value={c.title}
                  onChange={(e) =>
                    setSolCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Card title"
                />
              </Field>
              <Field label="Description">
                <TextArea
                  value={c.description}
                  onChange={(e) =>
                    setSolCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, description: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Card description"
                />
              </Field>
              <Field label="Image">
                <ImageUploadWithAlt
                  label="Upload card image"
                  alt={c.imageAlt}
                  onAltChange={(v) =>
                    setSolCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, imageAlt: v } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Link">
                <TextInput
                  value={c.link}
                  onChange={(e) =>
                    setSolCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, link: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="/solutions/..."
                />
              </Field>
              <Field label="Category Tag">
                <TextInput
                  value={c.category}
                  onChange={(e) =>
                    setSolCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, category: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="e.g. Technology"
                />
              </Field>
            </div>
          ))}
        </div>
        <BackgroundControls
          bgImageAlt={solBg.imageAlt}
          onBgImageAltChange={(v) => setSolBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={solBg.videoUrl}
          onBgVideoUrlChange={(v) => setSolBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={solBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setSolBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── INDUSTRY EXPERTISE ── */}
      <CollapsibleSection
        title="Industry Expertise"
        active={sections.industries}
        onToggleActive={() => toggle("industries")}
      >
        <Field label="Heading">
          <TextInput
            value={indHeading}
            onChange={(e) => setIndHeading(e.target.value)}
            placeholder="Industry Expertise"
          />
        </Field>
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Industries
            </p>
            <button
              type="button"
              onClick={() =>
                setIndItems((s) => [
                  ...s,
                  { iconAlt: "", title: "", description: "", link: "" },
                ])
              }
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add Industry
            </button>
          </div>
          {indItems.map((item, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Industry {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={indItems.length}
                    onMove={(f, t) => setIndItems(moveItem(indItems, f, t))}
                  />
                  {indItems.length > 1 && (
                    <button
                      onClick={() =>
                        setIndItems((s) => s.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Icon">
                <ImageUploadWithAlt
                  label="Upload icon"
                  hint="SVG or PNG"
                  alt={item.iconAlt}
                  onAltChange={(v) =>
                    setIndItems((s) =>
                      s.map((x, idx) => (idx === i ? { ...x, iconAlt: v } : x)),
                    )
                  }
                />
              </Field>
              <Field label="Title">
                <TextInput
                  value={item.title}
                  onChange={(e) =>
                    setIndItems((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Manufacturing"
                />
              </Field>
              <Field label="Description">
                <TextArea
                  value={item.description}
                  onChange={(e) =>
                    setIndItems((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, description: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Brief description"
                />
              </Field>
              <Field label="Link">
                <TextInput
                  value={item.link}
                  onChange={(e) =>
                    setIndItems((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, link: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="/industries/manufacturing"
                />
              </Field>
            </div>
          ))}
        </div>
        <BackgroundControls
          bgImageAlt={indBg.imageAlt}
          onBgImageAltChange={(v) => setIndBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={indBg.videoUrl}
          onBgVideoUrlChange={(v) => setIndBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={indBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setIndBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── COMPREHENSIVE SERVICES ── */}
      <CollapsibleSection
        title="Comprehensive Services"
        active={sections.services}
        onToggleActive={() => toggle("services")}
      >
        <Field label="Heading">
          <TextInput
            value={svcHeading}
            onChange={(e) => setSvcHeading(e.target.value)}
            placeholder="Comprehensive Services"
          />
        </Field>
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Service Cards
            </p>
            <button
              type="button"
              onClick={() =>
                setSvcCards((c) => [
                  ...c,
                  { title: "", description: "", imageAlt: "", link: "" },
                ])
              }
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add Card
            </button>
          </div>
          {svcCards.map((c, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Card {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={svcCards.length}
                    onMove={(f, t) => setSvcCards(moveItem(svcCards, f, t))}
                  />
                  {svcCards.length > 1 && (
                    <button
                      onClick={() =>
                        setSvcCards((s) => s.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Title">
                <TextInput
                  value={c.title}
                  onChange={(e) =>
                    setSvcCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Service title"
                />
              </Field>
              <Field label="Description">
                <TextArea
                  value={c.description}
                  onChange={(e) =>
                    setSvcCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, description: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Service description"
                />
              </Field>
              <Field label="Image">
                <ImageUploadWithAlt
                  label="Upload image"
                  alt={c.imageAlt}
                  onAltChange={(v) =>
                    setSvcCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, imageAlt: v } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Link">
                <TextInput
                  value={c.link}
                  onChange={(e) =>
                    setSvcCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, link: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="/services/..."
                />
              </Field>
            </div>
          ))}
        </div>
        <BackgroundControls
          bgImageAlt={svcBg.imageAlt}
          onBgImageAltChange={(v) => setSvcBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={svcBg.videoUrl}
          onBgVideoUrlChange={(v) => setSvcBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={svcBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setSvcBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── FEATURED PRODUCTS ── */}
      <CollapsibleSection
        title="Featured Products"
        active={sections.products}
        onToggleActive={() => toggle("products")}
      >
        <Field label="Heading">
          <TextInput
            value={prodHeading}
            onChange={(e) => setProdHeading(e.target.value)}
            placeholder="Featured Products"
          />
        </Field>
        <Field
          label="Products Selector"
          hint="Comma-separated product names from Products CMS"
        >
          <TextInput
            value={prodSelected}
            onChange={(e) => setProdSelected(e.target.value)}
            placeholder="SAP Business One, SAP S/4HANA"
          />
        </Field>
        <BackgroundControls
          bgImageAlt={prodBg.imageAlt}
          onBgImageAltChange={(v) => setProdBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={prodBg.videoUrl}
          onBgVideoUrlChange={(v) => setProdBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={prodBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setProdBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── WHY CHOOSE US ── */}
      <CollapsibleSection
        title="Why Choose Us"
        active={sections.whyChoose}
        onToggleActive={() => toggle("whyChoose")}
      >
        <Field label="Heading">
          <TextInput
            value={whyHeading}
            onChange={(e) => setWhyHeading(e.target.value)}
            placeholder="Why Choose ThinkProcess"
          />
        </Field>
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Benefit Cards
            </p>
            <button
              type="button"
              onClick={() =>
                setWhyCards((c) => [
                  ...c,
                  { iconAlt: "", title: "", description: "" },
                ])
              }
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add Card
            </button>
          </div>
          {whyCards.map((c, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Card {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={whyCards.length}
                    onMove={(f, t) => setWhyCards(moveItem(whyCards, f, t))}
                  />
                  {whyCards.length > 1 && (
                    <button
                      onClick={() =>
                        setWhyCards((s) => s.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Icon">
                <ImageUploadWithAlt
                  label="Upload icon"
                  hint="SVG or PNG"
                  alt={c.iconAlt}
                  onAltChange={(v) =>
                    setWhyCards((s) =>
                      s.map((x, idx) => (idx === i ? { ...x, iconAlt: v } : x)),
                    )
                  }
                />
              </Field>
              <Field label="Title">
                <TextInput
                  value={c.title}
                  onChange={(e) =>
                    setWhyCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Card title"
                />
              </Field>
              <Field label="Description">
                <TextArea
                  value={c.description}
                  onChange={(e) =>
                    setWhyCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, description: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Card description"
                />
              </Field>
            </div>
          ))}
        </div>
        <BackgroundControls
          bgImageAlt={whyBg.imageAlt}
          onBgImageAltChange={(v) => setWhyBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={whyBg.videoUrl}
          onBgVideoUrlChange={(v) => setWhyBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={whyBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setWhyBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── INSIGHTS ── */}
      <CollapsibleSection
        title="Insights Section"
        active={sections.insights}
        onToggleActive={() => toggle("insights")}
      >
        <Field label="Heading">
          <TextInput
            value={insHeading}
            onChange={(e) => setInsHeading(e.target.value)}
            placeholder="Latest Insights"
          />
        </Field>
        <Toggle
          checked={insMode === "auto"}
          onChange={(v) => setInsMode(v ? "auto" : "manual")}
          label="Auto-fetch latest posts"
          description="Automatically show most recent blog posts"
        />
        {insMode === "manual" && (
          <Field label="Selected Posts" hint="Comma-separated blog post slugs">
            <TextInput
              value={insSelectedPosts}
              onChange={(e) => setInsSelectedPosts(e.target.value)}
              placeholder="sap-b1-implementation-guide, erp-trends-2025"
            />
          </Field>
        )}
        <BackgroundControls
          bgImageAlt={insBg.imageAlt}
          onBgImageAltChange={(v) => setInsBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={insBg.videoUrl}
          onBgVideoUrlChange={(v) => setInsBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={insBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setInsBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── CLIENT SUCCESS ── */}
      <CollapsibleSection
        title="Client Success"
        active={sections.clientSuccess}
        onToggleActive={() => toggle("clientSuccess")}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Testimonials
          </p>
          <button
            type="button"
            onClick={() =>
              setCsTestimonials((t) => [
                ...t,
                {
                  quote: "",
                  author: "",
                  designation: "",
                  company: "",
                  imageAlt: "",
                },
              ])
            }
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{
              background: "hsl(var(--accent-muted))",
              color: "hsl(var(--accent))",
            }}
          >
            <Plus className="w-3.5 h-3.5" /> Add Testimonial
          </button>
        </div>
        {csTestimonials.map((t, i) => (
          <div
            key={i}
            className="p-3 rounded-lg border border-border space-y-3 mb-2"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Testimonial {i + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={i}
                  total={csTestimonials.length}
                  onMove={(f, tt) =>
                    setCsTestimonials(moveItem(csTestimonials, f, tt))
                  }
                />
                {csTestimonials.length > 1 && (
                  <button
                    onClick={() =>
                      setCsTestimonials((s) => s.filter((_, idx) => idx !== i))
                    }
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <Field label="Quote">
              <TextArea
                value={t.quote}
                onChange={(e) =>
                  setCsTestimonials((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, quote: e.target.value } : x,
                    ),
                  )
                }
                placeholder="Client testimonial"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Author">
                <TextInput
                  value={t.author}
                  onChange={(e) =>
                    setCsTestimonials((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, author: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="John Doe"
                />
              </Field>
              <Field label="Designation">
                <TextInput
                  value={t.designation}
                  onChange={(e) =>
                    setCsTestimonials((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, designation: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="CEO"
                />
              </Field>
              <Field label="Company">
                <TextInput
                  value={t.company}
                  onChange={(e) =>
                    setCsTestimonials((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, company: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Acme Inc"
                />
              </Field>
            </div>
            <Field label="Photo">
              <ImageUploadWithAlt
                label="Upload photo"
                hint="200×200px"
                alt={t.imageAlt}
                onAltChange={(v) =>
                  setCsTestimonials((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageAlt: v } : x)),
                  )
                }
              />
            </Field>
          </div>
        ))}
        <BackgroundControls
          bgImageAlt={csBg.imageAlt}
          onBgImageAltChange={(v) => setCsBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={csBg.videoUrl}
          onBgVideoUrlChange={(v) => setCsBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={csBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setCsBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── FINAL CTA ── */}
      <CollapsibleSection
        title="Final CTA Section"
        active={sections.finalCta}
        onToggleActive={() => toggle("finalCta")}
      >
        <Field label="Heading">
          <TextInput
            value={fctaHeading}
            onChange={(e) => setFctaHeading(e.target.value)}
            placeholder="Ready to Transform?"
          />
        </Field>
        <Field label="Subtext">
          <TextArea
            value={fctaSubtext}
            onChange={(e) => setFctaSubtext(e.target.value)}
            placeholder="Supporting text"
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={fctaPrimary.text}
          url={fctaPrimary.url}
          newTab={fctaPrimary.newTab}
          onTextChange={(v) => setFctaPrimary((c) => ({ ...c, text: v }))}
          onUrlChange={(v) => setFctaPrimary((c) => ({ ...c, url: v }))}
          onNewTabChange={(v) => setFctaPrimary((c) => ({ ...c, newTab: v }))}
        />
        <CTAGroup
          label="Secondary CTA"
          text={fctaSecondary.text}
          url={fctaSecondary.url}
          newTab={fctaSecondary.newTab}
          onTextChange={(v) => setFctaSecondary((c) => ({ ...c, text: v }))}
          onUrlChange={(v) => setFctaSecondary((c) => ({ ...c, url: v }))}
          onNewTabChange={(v) => setFctaSecondary((c) => ({ ...c, newTab: v }))}
        />
        <BackgroundControls
          bgImageAlt={fctaBg.imageAlt}
          onBgImageAltChange={(v) => setFctaBg((b) => ({ ...b, imageAlt: v }))}
          bgVideoUrl={fctaBg.videoUrl}
          onBgVideoUrlChange={(v) => setFctaBg((b) => ({ ...b, videoUrl: v }))}
          overlayOpacity={fctaBg.overlayOpacity}
          onOverlayOpacityChange={(v) =>
            setFctaBg((b) => ({ ...b, overlayOpacity: v }))
          }
        />
      </CollapsibleSection>

      {/* ── SEO ── */}
      <CollapsibleSection
        title="SEO Settings"
        active={sections.seo}
        onToggleActive={() => toggle("seo")}
      >
        <Field label="SEO Title" hint="50–60 characters">
          <TextInput
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
            placeholder="Home – ThinkProcess"
          />
        </Field>
        <Field label="SEO Description" hint="150–160 characters">
          <TextArea
            value={seoDesc}
            onChange={(e) => setSeoDesc(e.target.value)}
            placeholder="Meta description"
          />
        </Field>
        <Field label="OG Image">
          <ImageUploadWithAlt
            label="Upload OG image"
            hint="1200×630px"
            alt=""
            onAltChange={() => {}}
          />
        </Field>
        <Field label="Canonical URL">
          <TextInput
            value={seoCanonical}
            onChange={(e) => setSeoCanonical(e.target.value)}
            placeholder="https://..."
          />
        </Field>
        <Toggle
          checked={seoIndex}
          onChange={setSeoIndex}
          label="Index this page"
          description="Allow search engines to index"
        />
      </CollapsibleSection>

      <FormActions
        onCancel={() => navigate("/pages")}
        onSaveDraft={() => handleSave("draft")}
        onPublish={() => handleSave("published")}
        loading={loading}
      />
    </div>
  );
}
